import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatEUR, formatDate } from "@/lib/constants";
import { downloadSepa } from "@/components/tenancy/SurplusRefund";

const STATUS = {
  open: ["Offen", "bg-amber-50 text-amber-800 border-amber-200"],
  exported: ["Exportiert", "bg-sky-50 text-sky-800 border-sky-200"],
  paid: ["Ausgeführt", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  cancelled: ["Storniert", "bg-slate-50 text-slate-500 border-slate-200"],
};

export default function PayoutOrders() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [filter, setFilter] = useState("open,exported");

  const load = () => api.get("/payout-orders", { params: { status: filter || undefined } })
    .then(({ data }) => { setItems(data || []); setSel(new Set()); });
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exportable = items.filter((o) => ["open", "exported"].includes(o.status));

  const doExport = async () => {
    try { await downloadSepa([...sel]); toast.success("SEPA-Datei heruntergeladen"); load(); }
    catch (e) { toast.error("Export fehlgeschlagen"); }
  };
  const cancel = async (o) => {
    if (!await window._domivusConfirm(`Erstattung ${formatEUR(o.amount)} an ${o.creditor_name} stornieren? Die Buchung im Mietkonto wird entfernt.`)) return;
    try { await api.post(`/payout-orders/${o.id}/cancel`); toast.success("Storniert"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Stornieren fehlgeschlagen"); }
  };

  return (
    <div className="space-y-4" data-testid="payout-orders-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Link to="/bank" className="text-xs text-slate-500 hover:underline">← Bankbuchungen</Link>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Zahlungsaufträge</h1>
          <p className="text-sm text-slate-500 mt-1">Erstattungen als SEPA-Überweisungsdatei (pain.001) für das Online-Banking</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 border border-slate-300 rounded-md px-2 text-sm" data-testid="payout-filter">
            <option value="open,exported">Offen & exportiert</option>
            <option value="paid">Ausgeführt</option>
            <option value="cancelled">Storniert</option>
            <option value="">Alle</option>
          </select>
          <Button onClick={doExport} disabled={!sel.size} className="bg-slate-900 hover:bg-slate-800" data-testid="payout-export-btn">
            SEPA-Datei ({sel.size})
          </Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={exportable.length > 0 && exportable.every((o) => sel.has(o.id))}
                       onChange={(e) => setSel(e.target.checked ? new Set(exportable.map((o) => o.id)) : new Set())}
                       data-testid="payout-select-all" />
              </th>
              <th className="px-3 py-2">Datum</th><th className="px-3 py-2">Empfänger</th><th className="px-3 py-2">MV</th>
              <th className="px-3 py-2">Verwendungszweck</th><th className="px-3 py-2 text-right">Betrag</th>
              <th className="px-3 py-2">Status</th><th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Keine Zahlungsaufträge</td></tr>}
            {items.map((o) => {
              const [lbl, cls] = STATUS[o.status] || [o.status, ""];
              const canSel = ["open", "exported"].includes(o.status);
              return (
                <tr key={o.id} className="border-t border-slate-100" data-testid={`payout-row-${o.id}`}>
                  <td className="px-3 py-2">{canSel && <input type="checkbox" checked={sel.has(o.id)} onChange={() => toggle(o.id)} data-testid={`payout-select-${o.id}`} />}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(o.execution_date)}</td>
                  <td className="px-3 py-2"><div>{o.creditor_name}</div><div className="text-[11px] font-mono text-slate-500">{o.creditor_iban}</div></td>
                  <td className="px-3 py-2"><Link to={`/tenancies/${o.tenancy_id}`} className="text-teal-700 hover:underline">{o.contract_number || "MV"}</Link></td>
                  <td className="px-3 py-2 text-xs text-slate-600">{o.purpose}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatEUR(o.amount)}</td>
                  <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${cls}`}>{lbl}</span></td>
                  <td className="px-3 py-2 text-right">
                    {o.status !== "paid" && o.status !== "cancelled" && (
                      <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => cancel(o)} data-testid={`payout-cancel-${o.id}`}>Stornieren</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
